import { useState } from "react";
import { Link } from "react-router-dom";
import { Select } from "web-ui";

import type { ReadinessItem, Release } from "api/dashboardBackend";
import type { IndependentVariableType } from "components/release/metric-chart/MetricChart";
import MetricChart from "components/release/metric-chart/MetricChart";
import Preamble from "components/release/preamble/Preamble";
import styles from "components/release/readiness-checklist/ReadinessChecklist.module.scss";
import checkmarkIcon from "images/checkmark-icon.svg";
import downArrow from "images/downArrow.svg";
import externalLink from "images/externalLink.svg";
import iconInfo from "images/icon-info.svg";
import xIcon from "images/x-icon.svg";
import { type PlatformId } from "util/platform";

enum ChecklistItemType {
  CheckedItem = "checked-item",
  CheckedMetric = "checked-metric",
  PreambleMetric = "preamble",
}

const BASE_TABLE_COLUMNS = 4;

const isCheckedType = (type: ChecklistItemType) =>
  type === ChecklistItemType.CheckedItem ||
  type === ChecklistItemType.CheckedMetric;

const getItemIcon = (item: ReadinessItem, itemType: ChecklistItemType) => {
  if (itemType === ChecklistItemType.PreambleMetric) {
    return null;
  }
  if (item.valid) {
    return (
      <img alt="Checkmark" className={styles.metricIcon} src={checkmarkIcon} />
    );
  }
  return <img alt="X" className={styles.metricIcon} src={xIcon} />;
};

const getChartRefId = (item: ReadinessItem) => item.type.replace("_", "-");

const ChecklistItem = ({
  item,
  type,
}: {
  item: ReadinessItem;
  type: ChecklistItemType;
}) => {
  const valueText =
    isCheckedType(type) && item.description
      ? item.description
      : item.formattedValue || null;

  const chartLink =
    type === ChecklistItemType.CheckedMetric &&
    !item.type.toLowerCase().includes("download_size_check") ? (
      <div className={styles.metricLink}>
        <Link to={`#${getChartRefId(item)}`}>
          <img alt="Go to Chart" src={downArrow} />
        </Link>
      </div>
    ) : undefined;

  return (
    <tr>
      <td
        className={
          item.valid
            ? styles["metricTable__statusDatum--valid"]
            : styles["metricTable__statusDatum--invalid"]
        }
      >
        {getItemIcon(item, type)}
      </td>
      <td>
        <a href={item.url} rel="noopener noreferrer" target="_blank">
          <div className={styles.metricTable__readinessItemDatum}>
            <div className={styles["metricTable__readinessItemDatum--left"]}>
              {item.label}
            </div>
            <div className={styles["metricTable__readinessItemDatum--right"]}>
              <img alt="External Link" src={externalLink} />
            </div>
          </div>
        </a>
      </td>
      <td>
        {valueText ? (
          <div className={styles.metricTable__readinessValueDatum}>
            <strong>{valueText}</strong>
          </div>
        ) : null}
      </td>
      <td>{chartLink}</td>
      {item.createInvestigationUrl !== undefined && (
        <td>
          {item.createInvestigationUrl && (
            <a
              href={item.createInvestigationUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              <img alt="Investigate Link" src={externalLink} />
            </a>
          )}
        </td>
      )}
    </tr>
  );
};

interface Props {
  dailyActiveUsers?: ReadinessItem;
  metricReadinessItems?: ReadinessItem[];
  otherReadinessItems?: ReadinessItem[];
  platformId: PlatformId;
  sizeMetrics?: ReadinessItem[];
  version: string;
  release: Release;
}

const ReadinessChecklist = ({
  dailyActiveUsers,
  metricReadinessItems,
  otherReadinessItems,
  platformId,
  sizeMetrics,
  version,
  release,
}: Props) => {
  const [independentVariable, setIndependentVariable] =
    useState<IndependentVariableType>("dau");

  const preamble = (
    <Preamble
      dailyActiveUsers={dailyActiveUsers}
      platformId={platformId}
      release={release}
      sizeMetrics={sizeMetrics}
    />
  );

  // TODO(zak): Always show the metric investigation column after the flag flip is removed.
  const showMetricInvestigationColumn =
    metricReadinessItems?.some(item => item.createInvestigationUrl) ?? false;
  if (!showMetricInvestigationColumn) {
    metricReadinessItems?.forEach(
      item => (item.createInvestigationUrl = undefined),
    );
    otherReadinessItems?.forEach(
      item => (item.createInvestigationUrl = undefined),
    );
  }
  const tableColumnCount =
    BASE_TABLE_COLUMNS + (showMetricInvestigationColumn ? 1 : 0);

  const checklist =
    metricReadinessItems || otherReadinessItems ? (
      <div className={styles.metricTable}>
        <table>
          <thead>
            <tr>
              <th />
              <th>Readiness Item</th>
              <th>Value</th>
              <th>Chart</th>
              {showMetricInvestigationColumn && <th>Experiments</th>}
            </tr>
          </thead>
          {metricReadinessItems ? (
            <tbody>
              <tr>
                <th
                  className={styles.metricTable__groupHeader}
                  colSpan={tableColumnCount}
                  scope="rowgroup"
                >
                  Release Metric Checks
                </th>
              </tr>
              {metricReadinessItems?.map((readinessItem, idx) => (
                <ChecklistItem
                  item={readinessItem}
                  key={idx}
                  type={ChecklistItemType.CheckedMetric}
                />
              ))}
            </tbody>
          ) : null}
          {otherReadinessItems ? (
            <tbody>
              <tr>
                <th
                  className={styles.metricTable__groupHeader}
                  colSpan={tableColumnCount}
                  scope="rowgroup"
                >
                  Other Checks
                </th>
              </tr>
              {otherReadinessItems.map((readinessItem, idx) => (
                <ChecklistItem
                  item={readinessItem}
                  key={idx}
                  type={ChecklistItemType.CheckedItem}
                />
              ))}
            </tbody>
          ) : null}
        </table>
      </div>
    ) : null;

  const chartList = metricReadinessItems ? (
    <div>
      <div className={styles.metricChartsTitle}>
        <strong>Metric Readiness Charts</strong>
        <a
          href="https://duolingo.atlassian.net/wiki/spaces/DUO/pages/1001324627/Release+Dashboard+Release+Readiness#Readiness-Charts"
          rel="noopener noreferrer"
          target="_blank"
        >
          <img className={styles.infoIcon} src={iconInfo} />
        </a>
      </div>

      <div className={styles.metricChartsControls}>
        <div className={styles.metricChartsControls__controlItem}>
          <span>Independent Variable: </span>
          <Select
            className={styles.metricChartsControls__controlItem__selectBox}
            onChange={e =>
              setIndependentVariable(e.target.value as IndependentVariableType)
            }
            options={[
              {
                text: "DAU",
                value: "dau",
              },
              {
                text: "Time",
                value: "time",
              },
            ]}
            value={independentVariable}
          />
        </div>
      </div>

      <div className={styles.metricGroup}>
        {metricReadinessItems.map(
          (readinessItem, idx) =>
            !readinessItem.type
              .toLowerCase()
              .includes("download_size_check") && (
              <MetricChart
                independentVariable={independentVariable}
                key={idx}
                metricName={readinessItem.type}
                platformId={platformId}
                refId={getChartRefId(readinessItem)}
                version={version}
              />
            ),
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      {preamble}
      {checklist}
      {chartList}
    </>
  );
};

export default ReadinessChecklist;
